import { useEffect, useState, useCallback, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Users, Wallet, Settings2 } from 'lucide-react';
import PeriodPicker from '../components/common/PeriodPicker';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useLivingType, useSettingsStore } from '../store/settingsStore';
import { useBankAccountStore } from '../store/bankAccountStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { useLivingTerms, getPartnerName } from '../hooks/useLivingTerms';
import { CommonDashboardSection, PersonalDashboardSection, WidgetConfigModal } from '../components/dashboard';
import TopbarPortal from '../components/layout/TopbarPortal';
import { useThemeStore } from '../store/themeStore';
import HistoryLimitOverlay from '../components/common/HistoryLimitOverlay';
import type {
  BudgetSummary,
  PersonalBudgetSummary,
  DashboardStats,
  PersonalDashboardStats,
  DashboardWidgetsConfig,
  FullDashboardData,
  FullPersonalDashboardData,
} from '../types';
import { DEFAULT_COMMON_WIDGETS, DEFAULT_PERSONAL_WIDGETS } from '../types';

type ViewMode = 'weekly' | 'monthly' | 'annual';
type DashboardTab = 'common' | 'personal';

// Configuration par défaut
const DEFAULT_WIDGETS_CONFIG: DashboardWidgetsConfig = {
  common: DEFAULT_COMMON_WIDGETS,
  personal: DEFAULT_PERSONAL_WIDGETS,
};

// Helpers pour la gestion des semaines
function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  // Lundi = 1, donc on recule de (day - 1) jours, sauf dimanche (day = 0) où on recule de 6 jours
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getWeekEnd(weekStart: Date): Date {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
}

export default function Dashboard() {
  const { t, i18n } = useTranslation('dashboard');
  const dateLocale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
  const currentDate = new Date();
  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [personalSummary, setPersonalSummary] = useState<PersonalBudgetSummary | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [personalDashboardStats, setPersonalDashboardStats] = useState<PersonalDashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('monthly');
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => getWeekStart(currentDate));
  const [activeTab, setActiveTab] = useState<DashboardTab>('common');
  const [widgetsConfig, setWidgetsConfig] = useState<DashboardWidgetsConfig>(DEFAULT_WIDGETS_CONFIG);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const settings = useSettingsStore((state) => state.settings);
  // Sur Méridien, les contrôles de période et l'onglet commun/perso remontent
  // dans la topbar (téléportés) au lieu d'occuper deux lignes dans la page.
  const isMeridian = useThemeStore((s) => s.themeId === 'meridian');

  // Hooks pour les termes adaptés
  const livingType = useLivingType();
  const terms = useLivingTerms(livingType);
  const partner1Name = getPartnerName(1, user?.partner1FirstName, livingType);
  const partner2Name = getPartnerName(2, user?.partner2FirstName, livingType);

  // Vérifier si on doit afficher la section personnelle / commune
  const showPersonalSection = settings?.accountType !== 'joint_only';
  const showCommonSection = settings?.accountType !== 'personal_with_transfers';

  // En mode personal_with_transfers, forcer l'onglet personnel
  useEffect(() => {
    if (settings?.accountType === 'personal_with_transfers') {
      setActiveTab('personal');
    }
  }, [settings?.accountType]);

  // Comptes bancaires pour le carousel et les badges
  const { sharedBankAccounts, fetchSharedBankAccounts, bankAccounts, fetchBankAccounts } = useBankAccountStore();
  const { planInfo, isMonthRestricted, isWeekRestricted, isYearRestricted } = useSubscriptionStore();

  useEffect(() => {
    fetchSharedBankAccounts();
    fetchBankAccounts();
  }, [fetchSharedBankAccounts, fetchBankAccounts]);

  // Filtrer les comptes accessibles selon le plan
  const accessibleBankAccounts = useMemo(() => {
    if (!planInfo || planInfo.limits.personalAccounts === -1) return bankAccounts;
    const limit = planInfo.limits.personalAccounts;
    const sorted = [...bankAccounts].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
    const accessibleIds = new Set(sorted.slice(0, limit).map(a => a.id));
    return bankAccounts.filter(a => accessibleIds.has(a.id));
  }, [bankAccounts, planInfo]);

  const accessibleSharedAccounts = useMemo(() => {
    if (!planInfo || planInfo.limits.sharedAccounts === -1) return sharedBankAccounts;
    const limit = planInfo.limits.sharedAccounts;
    const sorted = [...sharedBankAccounts].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
    const accessibleIds = new Set(sorted.slice(0, limit).map(a => a.id));
    return sharedBankAccounts.filter(a => accessibleIds.has(a.id));
  }, [sharedBankAccounts, planInfo]);

  // Charger la configuration des widgets au montage
  useEffect(() => {
    const fetchWidgetsConfig = async () => {
      try {
        const response = await api.get('/user/dashboard-widgets');
        if (response.data.success && response.data.data) {
          setWidgetsConfig(response.data.data);
        }
      } catch {
        // Utiliser la config par défaut en cas d'erreur
      }
    };
    fetchWidgetsConfig();
  }, []);

  // Sauvegarder la configuration des widgets
  const handleSaveWidgetsConfig = useCallback(async (config: DashboardWidgetsConfig) => {
    try {
      await api.put('/user/dashboard-widgets', config);
      setWidgetsConfig(config);
    } catch {
      // Erreur silencieuse, la config locale est déjà mise à jour
    }
  }, []);

  // Vérifier si la période sélectionnée est restreinte par le plan
  const isPeriodRestricted = useMemo(() => {
    if (viewMode === 'weekly') return isWeekRestricted(selectedWeekStart);
    if (viewMode === 'monthly') return isMonthRestricted(selectedMonth, selectedYear);
    return isYearRestricted(selectedYear);
  }, [viewMode, selectedMonth, selectedYear, selectedWeekStart, isMonthRestricted, isWeekRestricted, isYearRestricted]);

  useEffect(() => {
    // Ne pas fetch si la période est restreinte
    if (isPeriodRestricted) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        if (viewMode === 'weekly') {
          const weekEnd = getWeekEnd(selectedWeekStart);
          params.append('weekStart', selectedWeekStart.toISOString().split('T')[0]);
          params.append('weekEnd', weekEnd.toISOString().split('T')[0]);
        } else if (viewMode === 'monthly') {
          params.append('month', selectedMonth.toString());
          params.append('year', selectedYear.toString());
        } else {
          params.append('year', selectedYear.toString());
        }

        // Utiliser les endpoints unifiés (un seul appel par onglet)
        // Cela évite les doublons de création d'opérations récurrentes
        if (showCommonSection) {
          const dashboardResponse = await api.get<{ success: boolean; data: FullDashboardData }>(
            `/settings/dashboard?${params.toString()}`
          );

          if (dashboardResponse.data.success) {
            setSummary(dashboardResponse.data.data.summary);
            setDashboardStats(dashboardResponse.data.data.stats);
          }
        }

        // Récupérer les données du dashboard personnel si nécessaire
        if (showPersonalSection) {
          const personalResponse = await api.get<{ success: boolean; data: FullPersonalDashboardData }>(
            `/settings/personal-dashboard?${params.toString()}`
          );
          if (personalResponse.data.success) {
            setPersonalSummary(personalResponse.data.data.summary);
            setPersonalDashboardStats(personalResponse.data.data.stats);
          }
        }
      } catch {
        // Handle error
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [viewMode, selectedMonth, selectedYear, selectedWeekStart, showPersonalSection, showCommonSection, isPeriodRestricted]);

  const handlePrevious = () => {
    if (viewMode === 'weekly') {
      const newWeekStart = new Date(selectedWeekStart);
      newWeekStart.setDate(newWeekStart.getDate() - 7);
      setSelectedWeekStart(newWeekStart);
    } else if (viewMode === 'monthly') {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      setSelectedYear(selectedYear - 1);
    }
  };

  const handleNext = () => {
    if (viewMode === 'weekly') {
      const newWeekStart = new Date(selectedWeekStart);
      newWeekStart.setDate(newWeekStart.getDate() + 7);
      setSelectedWeekStart(newWeekStart);
    } else if (viewMode === 'monthly') {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    } else {
      setSelectedYear(selectedYear + 1);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(dateLocale, { style: 'currency', currency: 'EUR' }).format(amount);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // ─── Contrôles Méridien, téléportés dans la topbar ───
  const periodLabel =
    viewMode === 'monthly'
      ? new Date(selectedYear, selectedMonth - 1).toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })
      : viewMode === 'annual'
        ? String(selectedYear)
        : selectedWeekStart.toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' });

  const meridianControls = (
    <>
      {/* Période : granularité + navigation, présentées ensemble par le tutoriel */}
      <div className="flex items-center gap-3" data-tutorial="dashboard-period">
        <div className="seg">
          {(['weekly', 'monthly', 'annual'] as const).map((m) => (
            <button key={m} onClick={() => setViewMode(m)} className={viewMode === m ? 'on' : undefined}>
              {t(`viewMode.${m}`, { ns: 'common' })}
            </button>
          ))}
        </div>
        <div className="stepper">
          <button onClick={handlePrevious} aria-label={t('previous', { ns: 'common' })}>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="lbl num">{periodLabel}</span>
          <button onClick={handleNext} aria-label={t('next', { ns: 'common' })}>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      {showPersonalSection && showCommonSection && (
        <div className="seg" data-tutorial="dashboard-tabs">
          <button onClick={() => setActiveTab('common')} className={activeTab === 'common' ? 'on' : undefined}>
            {t('commonAccount')}
          </button>
          <button onClick={() => setActiveTab('personal')} className={activeTab === 'personal' ? 'on' : undefined}>
            {t('personalBudget')}
          </button>
        </div>
      )}
      <button
        onClick={() => setIsConfigModalOpen(true)}
        className="icon-btn"
        title={t('customizeDisplay')}
        data-tutorial="dashboard-customize"
      >
        <Settings2 className="w-4 h-4" />
      </button>
    </>
  );

  return (
    <div className="space-y-8 animate-fade-in">
      {isMeridian && <TopbarPortal>{meridianControls}</TopbarPortal>}

      {/* En-tête avec sélecteur de période */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div data-tutorial="dashboard-title">
          <h1 className={isMeridian ? 'text-[clamp(24px,3vw,33px)] font-[680] tracking-[-.03em]' : 'text-2xl font-bold text-gray-900'}>
            {t('greeting', { name: user?.firstName ? `, ${user.firstName}` : '' })}
          </h1>
          <p className={isMeridian ? 'text-sm mt-1' : 'text-gray-600'} style={isMeridian ? { color: 'var(--text-2)' } : undefined}>
            {terms.budgetDescription}
          </p>
        </div>

        {/* Toggle vue hebdomadaire/mensuelle/annuelle */}
        <div className={clsx('flex items-center gap-2', isMeridian && 'hidden')} data-tutorial="dashboard-period">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('weekly')}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                viewMode === 'weekly'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              {t('viewMode.weekly', { ns: 'common' })}
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                viewMode === 'monthly'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              {t('viewMode.monthly', { ns: 'common' })}
            </button>
            <button
              onClick={() => setViewMode('annual')}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                viewMode === 'annual'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              {t('viewMode.annual', { ns: 'common' })}
            </button>
          </div>
        </div>
      </div>

      {/* Sélecteur de période (masqué sur Méridien : présent dans la topbar) */}
      <div className={clsx('items-center justify-center gap-4', isMeridian ? 'hidden' : 'flex')}>
        <button
          onClick={handlePrevious}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex items-center gap-2 min-w-[200px] justify-center">
          <Calendar className="w-5 h-5 text-primary-600" />
          {viewMode === 'monthly' && (
            <PeriodPicker
              mode="monthly"
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              onSelect={(month, year) => { setSelectedMonth(month); setSelectedYear(year); }}
            />
          )}
          {viewMode === 'annual' && (
            <PeriodPicker
              mode="annual"
              selectedYear={selectedYear}
              onSelect={(year) => setSelectedYear(year)}
            />
          )}
          {viewMode === 'weekly' && (
            <PeriodPicker
              mode="weekly"
              selectedWeekStart={selectedWeekStart}
              onSelect={(ws) => setSelectedWeekStart(ws)}
            />
          )}
        </div>
        <button
          onClick={handleNext}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Onglets Compte Commun / Mon Budget + configuration
          (masqués sur Méridien : présents dans la topbar) */}
      <div className={clsx('items-center justify-center gap-3', isMeridian ? 'hidden' : 'flex')}>
        {showPersonalSection && showCommonSection && (
          <div className="inline-flex bg-gray-100 rounded-xl p-1.5 gap-1" data-tutorial="dashboard-tabs">
            <button
              onClick={() => setActiveTab('common')}
              className={clsx(
                'flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                activeTab === 'common'
                  ? 'bg-white text-primary-700 shadow-md'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              )}
            >
              <Users className="w-4 h-4" />
              {t('commonAccount')}
            </button>
            <button
              onClick={() => setActiveTab('personal')}
              className={clsx(
                'flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                activeTab === 'personal'
                  ? 'bg-white text-emerald-700 shadow-md'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              )}
            >
              <Wallet className="w-4 h-4" />
              {t('personalBudget')}
            </button>
          </div>
        )}
        <button
          onClick={() => setIsConfigModalOpen(true)}
          className="p-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
          title={t('customizeDisplay')}
          data-tutorial="dashboard-customize"
        >
          <Settings2 className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Overlay de limite d'historique */}
      {isPeriodRestricted && (
        <HistoryLimitOverlay historyMonths={planInfo?.limits.historyMonths ?? 6} />
      )}

      {/* Section Compte Commun */}
      {!isPeriodRestricted && showCommonSection && (activeTab === 'common' || !showPersonalSection) && (
        <div data-tutorial="dashboard-summary">
          <CommonDashboardSection
            summary={summary}
            stats={dashboardStats}
            partner1Name={partner1Name}
            partner2Name={partner2Name}
            partnerSingular={terms.partnerSingular}
            formatCurrency={formatCurrency}
            isAnnualView={viewMode === 'annual'}
            isWeeklyView={viewMode === 'weekly'}
            visibleWidgets={widgetsConfig.common}
            month={viewMode === 'monthly' ? selectedMonth : undefined}
            year={selectedYear}
            weekStart={viewMode === 'weekly' ? selectedWeekStart.toISOString().split('T')[0] : undefined}
            weekEnd={viewMode === 'weekly' ? getWeekEnd(selectedWeekStart).toISOString().split('T')[0] : undefined}
            sharedBankAccounts={accessibleSharedAccounts.map(a => ({ id: a.id, name: a.name, color: a.color }))}
          />
        </div>
      )}

      {/* Section Budget Personnel */}
      {!isPeriodRestricted && showPersonalSection && activeTab === 'personal' && (
        <div data-tutorial="dashboard-personal">
          <PersonalDashboardSection
            summary={personalSummary}
            stats={personalDashboardStats}
            formatCurrency={formatCurrency}
            isAnnualView={viewMode === 'annual'}
            isWeeklyView={viewMode === 'weekly'}
            visibleWidgets={widgetsConfig.personal}
            month={viewMode === 'monthly' ? selectedMonth : undefined}
            year={selectedYear}
            weekStart={viewMode === 'weekly' ? selectedWeekStart.toISOString().split('T')[0] : undefined}
            weekEnd={viewMode === 'weekly' ? getWeekEnd(selectedWeekStart).toISOString().split('T')[0] : undefined}
            bankAccounts={accessibleBankAccounts.map(a => ({ id: a.id, name: a.name, color: a.color }))}
            partner1Name={partner1Name}
            partner2Name={partner2Name}
            isCurrentUserPartner1={user?.coupleRole === 'owner'}
          />
        </div>
      )}

      {/* Modal de configuration des widgets */}
      <WidgetConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        activeTab={activeTab}
        config={widgetsConfig}
        onSave={handleSaveWidgetsConfig}
        showCommonTab={showCommonSection}
        accountType={settings?.accountType}
      />
    </div>
  );
}
